import React, { useState, useEffect } from 'react';
import { supabase } from '../services/supabase';
import { ChatMessage as ChatMessageType } from '../types';
import ChatMessage from '../components/common/ChatMessage';
import '../styles/components/History.css';

interface ChatSession {
  id: string;
  title: string;
  timestamp: Date;
  messages: ChatMessageType[];
  chat_type: string;
  source: 'chat_sessions' | 'exam_sessions'; // Track where session came from
  score_percentage?: number;
  exam_level?: string;
  question_count?: number;
}

const History: React.FC = () => {
  const [sessions, setSessions] = useState<ChatSession[]>([]);
  const [selectedSessionId, setSelectedSessionId] = useState<string | null>(null);
  const [errorMessage, setErrorMessage] = useState<string>('');
  const [filter, setFilter] = useState<string>('all');
  const [loading, setLoading] = useState<boolean>(true);

  useEffect(() => {
    loadSessions();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filter]);

  const loadSessions = async () => {
    setLoading(true);
    const { data: { user }, error: authError } = await supabase.auth.getUser();
    if (authError || !user) {
      setErrorMessage('Failed to authenticate user. Please log in again.');
      setLoading(false);
      return;
    }

    try {
      // Load chat_sessions (regular chat sessions)
      let chatSessionsQuery = supabase
        .from('chat_sessions')
        .select('*')
        .eq('user_id', user.id);

      if (filter !== 'all' && filter !== 'mcq-exam') {
        chatSessionsQuery = chatSessionsQuery.eq('chat_type', filter);
      } else if (filter === 'mcq-exam') {
        // For MCQ exam filter, only get chat_sessions that are mcq-exam type
        chatSessionsQuery = chatSessionsQuery.eq('chat_type', 'mcq-exam');
      }

      const { data: chatSessionsData, error: chatSessionsError } = await chatSessionsQuery.order('created_at', { ascending: false });

      if (chatSessionsError) {
        throw new Error('Error loading chat sessions: ' + chatSessionsError.message);
      }

      // Load exam_sessions (MCQ exam sessions)
      let examSessionsQuery = supabase
        .from('exam_sessions')
        .select('*')
        .eq('user_id', user.id)
        .eq('exam_type', 'mcq-simulator');

      if (filter === 'mcq-exam' || filter === 'all') {
        // Only load exam sessions when filter is 'mcq-exam' or 'all'
        const { data: examSessionsData, error: examSessionsError } = await examSessionsQuery.order('created_at', { ascending: false });

        if (examSessionsError) {
          console.error('Error loading exam sessions:', examSessionsError);
        }

        // Combine and load all sessions
        const allSessions = await Promise.all([
          ...(chatSessionsData || []).map(loadChatSession),
          ...(examSessionsData || []).map(loadExamSession)
        ]);

        // Type guard function to filter out null values
        const isNotNull = <T,>(value: T | null): value is T => value !== null;

        // Filter out null sessions and sort by timestamp
        const sortedSessions = allSessions
          .filter(isNotNull)
          .sort((a, b) => b.timestamp.getTime() - a.timestamp.getTime());

        setSessions(sortedSessions);
      } else {
        // Only load chat sessions for other filters
        const chatSessions = await Promise.all(
          (chatSessionsData || []).map(loadChatSession)
        );

        // Type guard function to filter out null values
        const isNotNull = <T,>(value: T | null): value is T => value !== null;

        const filteredChatSessions = chatSessions
          .filter(isNotNull)
          .sort((a, b) => b.timestamp.getTime() - a.timestamp.getTime());

        setSessions(filteredChatSessions);
      }
    } catch (error: any) {
      setErrorMessage(error.message);
      console.error('Error loading sessions:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadChatSession = async (session: any): Promise<ChatSession | null> => {
    try {
      // Load messages for chat_session
      const { data: msgs, error: msgError } = await supabase
        .from('chat_messages')
        .select('*')
        .eq('session_id', session.id)
        .order('created_at', { ascending: true });

      if (msgError) {
        console.error('Error loading chat messages:', msgError);
      }

      return {
        id: session.id,
        title: session.title,
        timestamp: new Date(session.created_at),
        chat_type: session.chat_type,
        source: 'chat_sessions',
        messages: msgs?.map(msg => ({
          id: msg.id?.toString() || Date.now().toString(),
          role: msg.role,
          content: msg.content,
          timestamp: new Date(msg.created_at),
          chat_type: session.chat_type,
        })) || [],
        score_percentage: session.exam_score,
        exam_level: session.exam_level,
        question_count: session.exam_question_count,
      };
    } catch (error) {
      console.error('Error processing chat session:', error);
      return null;
    }
  };

  const loadExamSession = async (session: any): Promise<ChatSession | null> => {
    try {
      // Load messages for exam_session
      const { data: msgs, error: msgError } = await supabase
        .from('exam_messages')
        .select('*')
        .eq('exam_session_id', session.id)
        .order('created_at', { ascending: true });

      if (msgError) {
        console.error('Error loading exam messages:', msgError);
      }

      return {
        id: session.id,
        title: session.title,
        timestamp: new Date(session.created_at),
        chat_type: 'mcq-exam',
        source: 'exam_sessions',
        messages: msgs?.map(msg => ({
          id: msg.id?.toString() || Date.now().toString(),
          role: msg.role,
          content: msg.content,
          timestamp: new Date(msg.created_at),
          chat_type: 'mcq-exam',
        })) || [],
        score_percentage: session.score_percentage,
        exam_level: session.exam_level,
        question_count: session.question_count,
      };
    } catch (error) {
      console.error('Error processing exam session:', error);
      return null;
    }
  };

  const handleSelectSession = (sessionId: string) => {
    setSelectedSessionId(sessionId);
  };

  const getChatTypeIcon = (chatType: string) => {
    switch (chatType) {
      case 'confusion-solver':
        return '🧠';
      case 'socratic-tutor':
        return '🏛️';
      case 'math-explainer':
        return '➗';
      case 'adaptive-tutor':
        return '🔬';
      case 'idd-guide':
        return '🗺️';
      case 'pro-mentor':
        return '🎓';
      case 'bb-preneur':
        return '💼';
      case 'mcq-exam':
        return '📝';
      case 'theory-exam':
        return '📘';
      case 'document-question-extractor':
        return '📚';
      default:
        return '💬';
    }
  };

  const getChatTypeName = (chatType: string) => {
    switch (chatType) {
      case 'confusion-solver':
        return 'Confusion Solver';
      case 'socratic-tutor':
        return 'Socratic Tutor';
      case 'math-explainer':
        return 'Math Explainer';
      case 'adaptive-tutor':
        return 'Adaptive Tutor';
      case 'idd-guide':
        return 'IDD Guide';
      case 'pro-mentor':
        return 'Pro Mentor';
      case 'bb-preneur':
        return 'BB Preneur';
      case 'mcq-exam':
        return 'MCQ Exam Simulator';
      case 'theory-exam':
        return 'Theory Exam Simulator';
      default:
        return chatType;
    }
  };

  const selectedSession = sessions.find(session => session.id === selectedSessionId);

  // Count sessions by type
  const sessionCounts = {
    all: sessions.length,
    'confusion-solver': sessions.filter(s => s.chat_type === 'confusion-solver').length,
    'socratic-tutor': sessions.filter(s => s.chat_type === 'socratic-tutor').length,
    'math-explainer': sessions.filter(s => s.chat_type === 'math-explainer').length,
    'adaptive-tutor': sessions.filter(s => s.chat_type === 'adaptive-tutor').length,
    'idd-guide': sessions.filter(s => s.chat_type === 'idd-guide').length,
    'pro-mentor': sessions.filter(s => s.chat_type === 'pro-mentor').length,
    'bb-preneur': sessions.filter(s => s.chat_type === 'bb-preneur').length,
    'mcq-exam': sessions.filter(s => s.chat_type === 'mcq-exam').length,
    'theory-exam': sessions.filter(s => s.chat_type === 'theory-exam').length,
  };

  return (
    <div className="history-wrapper">
      {errorMessage && (
        <div className="error-message">
          <span className="error-icon">⚠️</span>
          <span>{errorMessage}</span>
        </div>
      )}
      <h1 className="history-title">Chat History</h1>
      
      {/* Filter controls */}
      <div className="history-filters">
        <label>Filter by chat type: </label>
        <select 
          value={filter} 
          onChange={(e) => setFilter(e.target.value)}
          className="filter-select"
        >
          <option value="all">All Chats ({sessionCounts.all})</option>
          <option value="confusion-solver">Confusion Solver ({sessionCounts['confusion-solver']})</option>
          <option value="socratic-tutor">Socratic Tutor ({sessionCounts['socratic-tutor']})</option>
          <option value="math-explainer">Math Explainer ({sessionCounts['math-explainer']})</option>
          <option value="adaptive-tutor">Adaptive Tutor ({sessionCounts['adaptive-tutor']})</option>
          <option value="idd-guide">IDD Guide ({sessionCounts['idd-guide']})</option>
          <option value="pro-mentor">Pro Mentor ({sessionCounts['pro-mentor']})</option>
          <option value="bb-preneur">BB Preneur ({sessionCounts['bb-preneur']})</option>
          <option value="mcq-exam">MCQ Exam Simulator ({sessionCounts['mcq-exam']})</option>
          <option value="theory-exam">Theory Exam Simulator ({sessionCounts['theory-exam']})</option>
        </select>
      </div>

      {loading ? (
        <div className="loading-container">
          <div className="loading-spinner"></div>
          <p>Loading sessions...</p>
        </div>
      ) : (
        <div className="history-container">
          <div className="session-list">
            <h2>Sessions ({sessions.length})</h2>
            {sessions.length === 0 ? (
              <p className="no-sessions">No chat sessions found.</p>
            ) : (
              <ul>
                {sessions.map(session => (
                  <li
                    key={session.id}
                    className={`session-item ${selectedSessionId === session.id ? 'selected' : ''}`}
                    onClick={() => handleSelectSession(session.id)}
                  >
                    <div className="session-header">
                      <span className="chat-type-icon">
                        {getChatTypeIcon(session.chat_type)}
                      </span>
                      <div className="session-title">
                        {session.title}
                        {session.source === 'exam_sessions' && session.score_percentage !== undefined && (
                          <span className={`session-score ${session.score_percentage >= 70 ? 'score-pass' : 'score-fail'}`}>
                            {session.score_percentage}%
                          </span>
                        )}
                      </div>
                    </div>
                    <div className="session-details">
                      <span className="chat-type-name">
                        {getChatTypeName(session.chat_type)}
                        {session.exam_level && (
                          <span className="exam-level"> • {session.exam_level}</span>
                        )}
                      </span>
                      <div className="session-timestamp">
                        {session.timestamp.toLocaleDateString()} • {session.timestamp.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                      </div>
                    </div>
                  </li>
                ))}
              </ul>
            )}
          </div>
          <div className="session-messages">
            {selectedSession ? (
              <>
                <div className="session-header">
                  <span className="chat-type-icon">
                    {getChatTypeIcon(selectedSession.chat_type)}
                  </span>
                  <h2>{selectedSession.title}</h2>
                  <span className="chat-type-badge">
                    {getChatTypeName(selectedSession.chat_type)}
                    {selectedSession.exam_level && (
                      <span className="exam-level"> • {selectedSession.exam_level}</span>
                    )}
                  </span>
                  {selectedSession.score_percentage !== undefined && (
                    <div className="session-score-display">
                      Score: <span className={`score-value ${selectedSession.score_percentage >= 70 ? 'score-pass' : 'score-fail'}`}>
                        {selectedSession.score_percentage}%
                      </span>
                    </div>
                  )}
                </div>
                <div className="messages-container">
                  {selectedSession.messages.length === 0 ? (
                    <p className="no-messages">No messages in this session.</p>
                  ) : (
                    selectedSession.messages.map(message => (
                      <ChatMessage key={message.id} message={message} />
                    ))
                  )}
                </div>
              </>
            ) : (
              <div className="no-session-selected">
                <div className="welcome-message">
                  <h3>Welcome to History</h3>
                  <p>Select a session to view its messages.</p>
                </div>
                <div className="session-stats">
                  <h4>Session Statistics</h4>
                  <div className="stats-grid">
                    <div className="stat-item">
                      <span className="stat-value">{sessionCounts.all}</span>
                      <span className="stat-label">Total Sessions</span>
                    </div>
                    <div className="stat-item">
                      <span className="stat-value">{sessionCounts['confusion-solver']}</span>
                      <span className="stat-label">Confusion Solver</span>
                    </div>
                    <div className="stat-item">
                      <span className="stat-value">{sessionCounts['socratic-tutor']}</span>
                      <span className="stat-label">Socratic Tutor</span>
                    </div>
                    <div className="stat-item">
                      <span className="stat-value">{sessionCounts['math-explainer']}</span>
                      <span className="stat-label">Math Explainer</span>
                    </div>
                    <div className="stat-item">
                      <span className="stat-value">{sessionCounts['adaptive-tutor']}</span>
                      <span className="stat-label">Adaptive Tutor</span>
                    </div>
                    <div className="stat-item">
                      <span className="stat-value">{sessionCounts['idd-guide']}</span>
                      <span className="stat-label">IDD Guide</span>
                    </div>
                    <div className="stat-item">
                      <span className="stat-value">{sessionCounts['pro-mentor']}</span>
                      <span className="stat-label">Pro Mentor</span>
                    </div>
                    <div className="stat-item">
                      <span className="stat-value">{sessionCounts['bb-preneur']}</span>
                      <span className="stat-label">BB Preneur</span>
                    </div>
                    <div className="stat-item">
                      <span className="stat-value">{sessionCounts['mcq-exam']}</span>
                      <span className="stat-label">MCQ Exams</span>
                    </div>
                    <div className="stat-item">
                      <span className="stat-value">{sessionCounts['theory-exam']}</span>
                      <span className="stat-label">Theory Exams</span>
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default History;